// File: src/app/services/export-customers.service.ts
import { Injectable, NgZone } from '@angular/core';
import Swal from 'sweetalert2';

/**
 * ExportCustomersService
 * Angular/TS version of the KTCustomersExport module.
 * - FormValidation (Bootstrap5 plugin)
 * - flatpickr date range on [name=date]
 * - Bootstrap Modal control
 * - SweetAlert2 flows for submit/cancel/close
 *
 * Assumes these are globally available via your scripts pipeline:
 *  - jQuery ($)
 *  - Bootstrap 5 (Modal)
 *  - FormValidation + Bootstrap5 plugin
 *  - flatpickr
 *  - SweetAlert2
 */
declare const $: any;
declare const bootstrap: any;
declare const FormValidation: any;

export interface CustomersExportSelectors {
  modal: string;      // default: '#kt_export_modal'
  form: string;       // default: '#kt_export_form'
  submit: string;     // default: '#kt_export_submit'
  cancel: string;     // default: '#kt_export_cancel'
  close: string;      // default: '#kt_export_close'
  dateField: string;  // default: '[name=date]'
}

@Injectable({ providedIn: 'root' })
export class ExportCustomersService {
  constructor(private zone: NgZone) {}

  /**
   * Initialize the Customers Export modal wiring.
   * Call this once the modal DOM exists (e.g., ngAfterViewInit of the page that renders it).
   */
  public init(selectors?: Partial<CustomersExportSelectors>): void {
    const s: CustomersExportSelectors = {
      modal: '#kt_export_modal',
      form: '#kt_export_form',
      submit: '#kt_export_submit',
      cancel: '#kt_export_cancel',
      close: '#kt_export_close',
      dateField: '[name=date]',
      ...selectors
    };

    this.zone.runOutsideAngular(() => {
      const element = document.querySelector(s.modal) as HTMLElement | null;
      const form = document.querySelector(s.form) as HTMLFormElement | null;

      if (!element || !form) return;

      const submitButton = form.querySelector(s.submit) as HTMLButtonElement | null;
      const cancelButton = form.querySelector(s.cancel) as HTMLButtonElement | null;
      const closeButton = element.querySelector(s.close) as HTMLButtonElement | null;

      const modal = new bootstrap.Modal(element);

      // --- FormValidation setup (matches your original rules) ---
      const validator = FormValidation.formValidation(form, {
        fields: {
          'date': {
            validators: {
              notEmpty: { message: 'Date range is required' }
            }
          }
        },
        plugins: {
          trigger: new FormValidation.plugins.Trigger(),
          bootstrap: new FormValidation.plugins.Bootstrap5({
            rowSelector: '.fv-row',
            eleInvalidClass: '',
            eleValidClass: ''
          })
        }
      });

      // --- Submit button logic ---
      if (submitButton) {
        submitButton.addEventListener('click', (e) => {
          e.preventDefault();

          if (!validator) return;
          validator.validate().then((status: string) => {
            if (status === 'Valid') {
              submitButton.setAttribute('data-kt-indicator', 'on');
              submitButton.disabled = true;

              setTimeout(() => {
                submitButton.removeAttribute('data-kt-indicator');

                Swal.fire({
                  text: 'Customer list has been successfully exported!',
                  icon: 'success',
                  buttonsStyling: false,
                  confirmButtonText: 'Ok, got it!',
                  customClass: { confirmButton: 'btn btn-primary' }
                }).then((result) => {
                  if (result.isConfirmed) {
                    modal.hide();
                    submitButton.disabled = false;
                  }
                });

                // If you need to submit to backend:
                // form.submit();
              }, 2000);
            } else {
              Swal.fire({
                text: 'Sorry, looks like there are some errors detected, please try again.',
                icon: 'error',
                buttonsStyling: false,
                confirmButtonText: 'Ok, got it!',
                customClass: { confirmButton: 'btn btn-primary' }
              });
            }
          });
        });
      }

      // --- Cancel / Close buttons (share same confirm-cancel flow) ---
      const bindCancelLike = (btn: HTMLButtonElement | null) => {
        if (!btn) return;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          Swal.fire({
            text: 'Are you sure you would like to cancel?',
            icon: 'warning',
            showCancelButton: true,
            buttonsStyling: false,
            confirmButtonText: 'Yes, cancel it!',
            cancelButtonText: 'No, return',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-active-light'
            }
          }).then((result: any) => {
            if (result.value) {
              form.reset(); // Reset form
              modal.hide(); // Hide modal
            } else if (result.dismiss === 'cancel') {
              Swal.fire({
                text: 'Your form has not been cancelled!.',
                icon: 'error',
                buttonsStyling: false,
                confirmButtonText: 'Ok, got it!',
                customClass: { confirmButton: 'btn btn-primary' }
              });
            }
          });
        });
      };

      bindCancelLike(cancelButton);
      bindCancelLike(closeButton);

      // --- flatpickr range on [name=date] ---
      const datepicker = form.querySelector(s.dateField) as HTMLElement | null;
      if (datepicker && typeof $(datepicker).flatpickr === 'function') {
        $(datepicker).flatpickr({
          altInput: true,
          altFormat: 'F j, Y',
          dateFormat: 'Y-m-d',
          mode: 'range'
        });
      }
    });
  }
}
